/**
 * Detección de «servidor inalcanzable»: el ApiClient reporta aquí cada fallo
 * de red (fetch rechazado o error de gateway/edge) y cada respuesta recibida.
 * Con FAILURE_THRESHOLD fallos consecutivos se declara el estado unreachable
 * (evento SERVER_REACHABILITY_EVENT → banner global) y arranca un sondeo
 * periódico al /health del worker; la primera respuesta — del sondeo o de
 * cualquier petición normal — lo resuelve y oculta el banner.
 *
 * `navigator.onLine === false` no cuenta como fallo: ese caso lo cubre el
 * banner de «sin conexión» (useOnlineStatus) y no debe solaparse con este.
 */

import { SERVER_REACHABILITY_EVENT } from '@/constants/events';
import { API_BASE_URL } from './client';

/** Fallos de red consecutivos antes de declarar unreachable (evita que un
 *  único request flaky haga parpadear el banner). */
const FAILURE_THRESHOLD = 2;
const PROBE_INTERVAL_MS = 15000;

let consecutiveFailures = 0;
let unreachable = false;
let probeTimer: number | null = null;

export function isServerUnreachable(): boolean {
  return unreachable;
}

function setUnreachable(next: boolean): void {
  if (unreachable === next) return;
  unreachable = next;
  window.dispatchEvent(
    new CustomEvent(SERVER_REACHABILITY_EVENT, { detail: { unreachable: next } })
  );
  if (next) startProbe();
  else stopProbe();
}

export function reportNetworkFailure(): void {
  // Sin red local no hay señal sobre el servidor; lo cubre el banner offline.
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  consecutiveFailures += 1;
  if (consecutiveFailures >= FAILURE_THRESHOLD) setUnreachable(true);
}

export function reportNetworkSuccess(): void {
  consecutiveFailures = 0;
  setUnreachable(false);
}

function startProbe(): void {
  if (probeTimer !== null) return;
  // /health vive en la raíz del worker, fuera del prefijo /api.
  const healthUrl = `${API_BASE_URL.replace(/\/api$/, '')}/health`;
  probeTimer = window.setInterval(() => {
    // Cualquier respuesta HTTP (aunque fuera un 500) demuestra que el servidor
    // vuelve a ser alcanzable; solo el rechazo de fetch mantiene el estado.
    fetch(healthUrl, { cache: 'no-store' })
      .then(() => reportNetworkSuccess())
      .catch(() => {});
  }, PROBE_INTERVAL_MS);
}

function stopProbe(): void {
  if (probeTimer === null) return;
  window.clearInterval(probeTimer);
  probeTimer = null;
}
